import { DateTimePickerProps, TimePickerProps } from "@mui/x-date-pickers";
import { Row } from "@tanstack/table-core/src/types";
import luaFmtWasmUrl from "@wasm-fmt/lua_fmt/wasm";
import initLuaFmt, { format as styluaFormat } from "@wasm-fmt/lua_fmt/web";
import { AxiosResponse } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { isArray, uniq } from "lodash";
import get from "lodash/get";
import { MRT_ColumnFiltersState } from "material-react-table";
import { enqueueSnackbar } from "notistack";
import prettier from "prettier";
import parserJson from "prettier/parser-babel";

import {
  ConfigDto,
  PlayhubGroupDto,
  PlayhubRoomDto,
  RoomDto,
  RoomTablesCacheTableDto,
  SysCfgRawDto,
  SysDomainRegularDto,
  UserResponseRegularDto,
} from "@/shared/api";
import { StatVisualizationGroupDto } from "@/shared/api/models/stat-visualization-group-dto";
import { StatVisualizationRoomDto } from "@/shared/api/models/stat-visualization-room-dto";
import { SearchParams } from "@/types/common-types";

import { COMMON_PERM, ExploitationStage, ROOM_NAMES } from "./constants";

export const formatJson = (json: any) => {
  try {
    return prettier.format(json, {
      parser: "json",
      plugins: [parserJson],
    });
  } catch (error) {
    console.error("Invalid JSON format:", error);
    return json;
  }
};

const LUA_LINE_WIDTH = 100;

let luaFmtReady: Promise<void> | null = null;

const ensureLuaFmt = () => {
  if (!luaFmtReady) {
    luaFmtReady = initLuaFmt(luaFmtWasmUrl).then(() => undefined);
  }
  return luaFmtReady;
};

// Warm up WASM formatter early
void ensureLuaFmt();

/** Strip strings and comments so keyword matching ignores them. */
const stripLuaStringsAndComments = (line: string): string => {
  let result = "";
  let i = 0;

  while (i < line.length) {
    const ch = line[i];
    const next = line[i + 1];

    // Line comment
    if (ch === "-" && next === "-") {
      break;
    }

    // Long string/comment [[...]] or [=[...]=]
    if (ch === "[" && (next === "[" || next === "=")) {
      const match = line.slice(i).match(/^\[=*\[/);
      if (match) {
        const eq = match[0].slice(1, -1);
        const close = `]${eq}]`;
        const closeIdx = line.indexOf(close, i + match[0].length);
        i = closeIdx === -1 ? line.length : closeIdx + close.length;
        result += " ";
        continue;
      }
    }

    // Single or double quoted string
    if (ch === '"' || ch === "'") {
      const quote = ch;
      i += 1;
      while (i < line.length) {
        if (line[i] === "\\") {
          i += 2;
          continue;
        }
        if (line[i] === quote) {
          i += 1;
          break;
        }
        i += 1;
      }
      result += " ";
      continue;
    }

    result += ch;
    i += 1;
  }

  return result;
};

/** Fallback indent-only formatter when Beautify cannot parse the script. */
const indentLua = (code: string, indentSize = 2): string => {
  const tab = " ".repeat(indentSize);
  const lines = code.replace(/\r\n/g, "\n").split("\n");
  let indent = 0;
  const result: string[] = [];

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      result.push("");
      continue;
    }

    const analysis = stripLuaStringsAndComments(line).trim();
    const startsWithCloser = /^(end|until|else|elseif)\b/.test(analysis);

    if (startsWithCloser) {
      indent = Math.max(0, indent - 1);
    }

    result.push(tab.repeat(indent) + line);

    const opens = (analysis.match(/\b(function|then|do|repeat)\b/g) ?? []).length;
    const closes = (analysis.match(/\b(end|until)\b/g) ?? []).length;
    let delta = opens - closes;

    if (/^(else|elseif)\b/.test(analysis)) {
      delta += 1;
    }

    if (/^(end|until)\b/.test(analysis)) {
      delta += 1;
    }

    indent = Math.max(0, indent + delta);
  }

  return result.join("\n");
};

/** Beautify Lua with max line width 150 (falls back to indent-only on parse errors). */
export const formatLua = async (code: string, indentSize = 2): Promise<string> => {
  if (!code) return code;

  try {
    await ensureLuaFmt();
    return styluaFormat(code, {
      line_width: LUA_LINE_WIDTH,
      indent_width: indentSize,
      indent_style: "space",
      line_ending: "lf",
    });
  } catch (error) {
    console.error("Lua beautify failed, falling back to indent:", error);
    try {
      return indentLua(code, indentSize);
    } catch (indentError) {
      console.error("Invalid Lua format:", indentError);
      return code;
    }
  }
};

export const getPublicUrl = () => {
  return window.location.origin;
};

export const trimSplitCsvRecord = (value: string, delimiter: string) => {
  const recordValues = value.split(delimiter);

  for (let i = 0; i < recordValues.length; i++) {
    recordValues[i] = recordValues[i].trim();
  }

  return recordValues;
};

export const isValidPort = (value: unknown): boolean => {
  const portNumber = Number(value);
  return Number.isInteger(portNumber) && portNumber >= 1 && portNumber <= 65535;
};

export const mapExploitationStageToStringName = (stage: ExploitationStage) => {
  switch (stage) {
    case ExploitationStage.PRODUCTION:
      return "Production";
    case ExploitationStage.TEST:
      return "Test";
    case ExploitationStage.UNAVAILABLE:
      return "Unavailable";
    case ExploitationStage.SERVICE:
      return "Service";
    default:
      return "Unknown value";
  }
};

export const getEnumNumberValues = (enumObj: any): number[] => {
  if (!enumObj) return [];
  return Object.values(enumObj).filter((item) => typeof item === "number") as number[];
};

export const arrayNumberNormalizer = (values: any): number[] => {
  if (!isArray(values)) return [];
  const numbers = values
    .map((item) => parseFloat(item))
    .filter((item) => {
      const check = !isNaN(item);

      if (!check) enqueueSnackbar("Integers only", { variant: "error" });
      return check;
    })
    .map((item) => Number(item.toFixed(1)));
  return uniq(numbers);
};

export const mapCommonRoomsToRoomDto = (
  domains: SysDomainRegularDto[],
  user: UserResponseRegularDto
): RoomDto[] => {
  return domains
    .filter((domain) => domain.is_room)
    .map((domain) => {
      const room = user.rooms.find((it) => it.room_name === domain.name);
      return {
        room_name: domain.name,
        configs: domain.config_names.map((it) => assembleConfigsDto(it, room?.configs)),
      };
    });
};

export const mapPhRooms = (
  rooms: string[],
  gameGroupsByRooms: Record<string, SysCfgRawDto[]>,
  user: UserResponseRegularDto
): PlayhubRoomDto[] => {
  return rooms.map((room) => {
    const groups = gameGroupsByRooms[room] ?? [];
    const userGroups = user.playhub.find((it) => it.room_name === room)?.groups ?? [];

    return {
      room_name: room,
      groups: assemblePhGroups(groups, userGroups),
    };
  });
};

export const assemblePhGroups = (
  gameGroups: SysCfgRawDto[],
  userGroups: PlayhubGroupDto[]
): PlayhubGroupDto[] => {
  return gameGroups.map((it) => {
    const userGroup = userGroups.find((ug) => ug.name === it.name);
    return {
      name: it.name,
      room_read: userGroup?.room_read ?? false,
      chat_read: userGroup?.chat_read ?? false,
      chat_write: userGroup?.chat_write ?? false,
      max_bot_read: userGroup?.max_bot_read ?? false,
      max_bot_write: userGroup?.max_bot_write ?? false,
      bot_execute_leave: userGroup?.bot_execute_leave ?? false,
      bot_execute_manually_boarding: userGroup?.bot_execute_manually_boarding ?? false,
      game_read: userGroup?.game_read ?? false,
      game_write: userGroup?.game_write ?? false,
      billing: userGroup?.billing ?? false,
      behaviour_post_dead_blind: userGroup?.behaviour_post_dead_blind ?? false,
      behaviour_pre_leave: userGroup?.behaviour_pre_leave ?? false,
      behaviour_exit: userGroup?.behaviour_exit ?? false,
      behaviour_cash_out: userGroup?.behaviour_cash_out ?? false,
      behaviour_buy_in: userGroup?.behaviour_buy_in ?? false,
      bot_observe: userGroup?.bot_observe ?? false,
      game_show_cards: userGroup?.game_show_cards ?? false,
    };
  });
};

export const mapStatVisualizationRooms = (
  rooms: string[],
  gameGroupsByRooms: Record<string, SysCfgRawDto[]>,
  user: UserResponseRegularDto
): StatVisualizationRoomDto[] => {
  return rooms.map((room) => {
    const groups = gameGroupsByRooms[room] ?? [];
    const userGroups =
      user.stat_visualization.find((it) => it.room_name === room)?.groups ?? [];

    return {
      room_name: room,
      groups: assembleStatVisualizationGroups(groups, userGroups),
    };
  });
};

export const assembleStatVisualizationGroups = (
  gameGroups: SysCfgRawDto[],
  userGroups: StatVisualizationGroupDto[]
): StatVisualizationGroupDto[] => {
  return gameGroups.map((it) => {
    const userGroup = userGroups.find((ug) => ug.name === it.name);
    return {
      name: it.name,
      eco: userGroup?.eco ?? false,
      adv: userGroup?.adv ?? false,
      aicon: userGroup?.aicon ?? false,
    };
  });
};

// export const mapPlayhubRoomsToRoomDto = (
//   domains: SysCfgRegularDto[],
//   user: UserResponseRegularDto
// ) => {
//   return domains.map((domain) => {
//     const room = user.playhub.find((it) => it.room_name === domain.name);
//     return {
//       room_name: domain.name,
//       configs: PLAYHUB_CONFIGS.map((it) => assembleConfigsDto(it.name, room?.configs)),
//     };
//   });
// };

export const mapServiceRoomToRoomDto = (
  domains: SysDomainRegularDto[],
  user: UserResponseRegularDto
): RoomDto => {
  const servicesDomain = domains.find((it) => it.name === ROOM_NAMES.SERVICES);

  if (servicesDomain) {
    return {
      room_name: servicesDomain.name,
      configs: servicesDomain.config_names.map((it) =>
        assembleConfigsDto(it, user.service.configs)
      ),
    };
  }

  return {
    room_name: ROOM_NAMES.SERVICES,
    configs: [],
  };
};

const assembleConfigsDto = (configName: string, configs?: ConfigDto[]) => {
  return (
    (configs ?? []).find((uit) => uit.name === configName) ?? {
      name: configName,
      ...COMMON_PERM,
    }
  );
};

export const downloadFile = (response: AxiosResponse<BlobPart>) => {
  const blob = new Blob([response.data]);
  const contentDisposition =
    response?.headers?.get &&
    typeof response.headers.get == "function" &&
    response.headers.get("content-disposition");

  let filename = "download.txt";

  if (contentDisposition) {
    const value = contentDisposition.toString();
    const startAt = value.replaceAll(" ", "").lastIndexOf("filename=");
    const newFilename =
      startAt !== -1
        ? value
            .substring(startAt)
            .replaceAll("filename=", "")
            .replaceAll('"', "")
            .replaceAll("'", "")
        : "";

    filename = newFilename && newFilename.trim() !== "" ? newFilename : filename;
  }

  const url = window.URL.createObjectURL(blob);
  const tempLink = document.createElement("a");

  tempLink.href = url;

  tempLink.setAttribute("download", filename);
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  window.URL.revokeObjectURL(url);
};

export const timeFilterProps: TimePickerProps<Dayjs> = {
  ampm: false,
};
export const dateFilterProps: DateTimePickerProps<Dayjs> = {
  ampm: false,
};
export const dateFilterFn = (
  row: unknown,
  id: string,
  filterValue: [Dayjs | null, Dayjs | null]
) => {
  if (!!filterValue[0] && !!filterValue[1]) {
    const value = Number(get((row as Row<unknown>).original, id)) / 1000;
    const start = filterValue[0]?.unix();
    const end = filterValue[1]?.unix();

    return value > start && value < end;
  }
  return true;
};

export const commonDateFilterFn = <T>(
  row: Row<T>,
  columnId: string,
  filterValue: any
): boolean => {
  if (!Array.isArray(filterValue)) return true;

  const startVal = filterValue[0];
  const endVal = filterValue[1];

  if (startVal != null && !dayjs.isDayjs(startVal)) return true;
  if (endVal != null && !dayjs.isDayjs(endVal)) return true;

  const value = row.getValue<any>(columnId);
  const start = startVal?.valueOf();
  const end = endVal?.valueOf();

  // If at least one of filters have been set but value is inappropriate than return false
  if ((start != null || end != null) && typeof value !== "number") return false;

  // If both filters have been set than compare both, otherwise compare separately
  if (start != null && end != null) return value > start && value < end;
  if (start != null) return value > start;
  if (end != null) return value < end;

  return true;
};

export const durationFilterFn = (
  row: unknown,
  id: string,
  filterValue: [Dayjs | null, Dayjs | null]
) => {
  const original = (row as Row<RoomTablesCacheTableDto>).original;
  if (!!filterValue[0] && !!filterValue[1] && original.finishedAt && original.startedAt) {
    const duration = dayjs.duration(original.finishedAt - original.startedAt, "milliseconds");
    const durationFilter = filterValue[1].diff(filterValue[0], "milliseconds");

    return duration.asMilliseconds() < durationFilter;
  }

  return true;
};

export const getDate = (value?: number) => {
  if (value) {
    return dayjs.unix(Number(value) / 1000).format("YYYY-MM-DD HH:mm:ss");
  }
  return "-";
};

export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

export const formatTimestamp = (value?: number): string | undefined => {
  if (!value) return undefined;
  return new Date(value).toLocaleString();
};

export const assembleSearchParamsFilters = (
  searchParams: SearchParams
): MRT_ColumnFiltersState =>
  Object.entries(searchParams)
    .filter((it) => it[1])
    .map((it) => ({ id: it[0], value: it[1] }));

export const getArrayOrEmpty = <T>(value: T | T[] | undefined): T[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

export const pushMapArrayVal = (map: Map<string, number[]>, key: string, val: number) => {
  const winnerHandsCount = map.get(key) || [];
  winnerHandsCount.push(val);
  map.set(key, winnerHandsCount);
};
